import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { checkHasPlayhead } from "../../components/audioPlayer/checkHasPlayhead.js";
import {
  findClosestScrollContainer,
  SCROLLING_ROLE_DATA_KEY,
  scrollToTranscriptElement,
  scrollTranscriptContainer,
} from "../../components/transcript/scroll.js";

export interface TranscriptAudioSync {
  isAutoScrollSuspended: boolean;
  resumeDirection: "down" | "up";
  resumeAutoScroll: () => void;
}

export function useTranscriptAudioSync(
  containerRef: RefObject<HTMLElement | null>,
  nativeAudio: HTMLAudioElement | null,
  recording: AccountGradingRecording | undefined,
  hasRequestedPosition: boolean,
  initialScrollTop?: number,
): TranscriptAudioSync {
  const [isAutoScrollSuspended, setIsAutoScrollSuspended] = useState(false);
  const [resumeDirection, setResumeDirection] = useState<"down" | "up">("down");
  const resumeAutoScrollRef = useRef<() => void>(() => undefined);

  // Track whether the mouse is over the transcript regardless of recording (audio source) changes.
  const getIsMouseOver = useGetIsMouseOver(containerRef);

  // With no exact scroll position to restore, a transcript shown with the playhead already placed starts at it.
  const shouldShowPlayheadRef = useRef(hasRequestedPosition);
  const initialScrollTopRef = useRef(initialScrollTop);

  useLayoutEffect(() => {
    const scrollTop = initialScrollTopRef.current;
    const container = containerRef.current;
    if (scrollTop === undefined || !container) {
      return;
    }

    const scrollContainer = findClosestScrollContainer(container);
    if (!scrollContainer) {
      return;
    }

    initialScrollTopRef.current = undefined;
    shouldShowPlayheadRef.current = false;
    scrollTranscriptContainer(scrollContainer, scrollTop, "instant");
  }, [containerRef]);

  // Sync scroll assuming the given recording fragments match the audio source loaded in the given audio element.
  const recordingId = recording?.id;
  const fragments = recording?.transcript?.fragments;
  useLayoutEffect(() => {
    setIsAutoScrollSuspended(false);

    const container = containerRef.current;
    if (!container || !recordingId || !fragments || !nativeAudio) {
      return;
    }

    const scrollContainer = findClosestScrollContainer(container);
    if (!scrollContainer) {
      return;
    }

    type PlayingFragment = {
      startTime: number;
      element: Element;
      isVisible: boolean;
      reachesLowerQuarter: boolean;
      resumeDirection: "down" | "up";
    };
    const getPlayingFragment = (): PlayingFragment | undefined => {
      // The current fragment is the one marked as current, playing or paused
      if (
        nativeAudio.paused &&
        !checkHasPlayhead(nativeAudio, hasRequestedPosition)
      ) {
        return undefined;
      }

      // Round up on the nearest 10 ms so we can skip past discrepancies in audio frames and floating point precision
      const playTime = Math.ceil(nativeAudio.currentTime * 100) / 100;

      // Find the start time of the fragment that is currently playing
      const startTime = fragments.find((_fragment, index, allFragments) => {
        const nextFragment = allFragments.at(index + 1);

        return (
          nextFragment === undefined ||
          playTime < nextFragment.start_time / 1000
        );
      })?.start_time;

      if (startTime === undefined) {
        return undefined;
      }

      const element = container.querySelector(
        `[data-recording-id="${recordingId}"][data-timestamp="${startTime}"]`,
      );

      if (!element) {
        return undefined;
      }

      const clipRect = scrollContainer.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const isVisible =
        rect.top < clipRect.bottom && clipRect.top < rect.bottom;
      // The fragment's end decides, so one that starts higher but runs into the lower quarter, or past the fold, counts too
      const reachesLowerQuarter =
        rect.bottom >= clipRect.top + clipRect.height * 0.75;
      // Recovery direction is only meaningful while the fragment is out of view
      const resumeDirection = rect.bottom <= clipRect.top ? "up" : "down";

      return {
        startTime,
        element,
        isVisible,
        reachesLowerQuarter,
        resumeDirection,
      };
    };

    let isUserScrolling = false;
    let scrollEndTimer: ReturnType<typeof setTimeout> | undefined;
    let isFollowingSuspended = false;

    // Current scroll sync state
    let playingFragment: PlayingFragment | undefined;
    let shouldTreatFragmentAsNew = false;

    // The resume trigger shows while following is suspended and the current
    // fragment is out of view, playing or paused
    const syncSuspensionState = () => {
      if (
        isFollowingSuspended &&
        playingFragment &&
        !playingFragment.isVisible
      ) {
        setResumeDirection(playingFragment.resumeDirection);
        setIsAutoScrollSuspended(true);
      } else {
        setIsAutoScrollSuspended(false);
      }
    };

    const onScroll = () => {
      // A system scroll must never suspend following. `scrollToTranscriptElement` marks the container it scrolls, so a scroll already under way when this effect runs is recognised too.
      if (scrollContainer.dataset[SCROLLING_ROLE_DATA_KEY] !== "system") {
        isUserScrolling = true;
        // Any manual scroll suspends following until play, a seek during playback, a jump, or resume
        isFollowingSuspended = true;
        playingFragment = getPlayingFragment();
        syncSuspensionState();
      }

      // Approximate `scrollend` with a throttle, since some browsers (like Safari) don't support it
      if (scrollEndTimer !== undefined) {
        clearTimeout(scrollEndTimer);
      }
      scrollEndTimer = setTimeout(onScrollEnd, 1000);
    };

    const onScrollEnd = () => {
      isUserScrolling = false;
      delete scrollContainer.dataset[SCROLLING_ROLE_DATA_KEY];

      // The `scrollend` (if supported) typically happens sooner, so clear the backup timer
      if (scrollEndTimer !== undefined) {
        clearTimeout(scrollEndTimer);
      }
      scrollEndTimer = undefined;
    };

    const onPlay = () => {
      isFollowingSuspended = false;
      playingFragment = getPlayingFragment();

      if (playingFragment) {
        playingFragment = {
          ...playingFragment,
          // When we start playing, mark the fragment as already visible to scroll it into view if necessary and sync the scroll on future time updates.
          isVisible: true,
        };

        // If the mouse was outside the transcript, treat the fragment as newly active, so it moves up if it reaches the lower quarter. (If the mouse is on the transcript, we don't want to do this to avoid moving the click target.)
        shouldTreatFragmentAsNew = !getIsMouseOver();
      }
      syncSuspensionState();
    };

    const onSeeking = () => {
      // A paused seek keeps a suspension, because the browser also seeks by itself once a source loads, which must not undo a scroll the reader made in the meantime. A jump is the exception: it scrolls the transcript there itself.
      if (
        nativeAudio.paused &&
        isFollowingSuspended &&
        scrollContainer.dataset[SCROLLING_ROLE_DATA_KEY] !== "system"
      ) {
        playingFragment = getPlayingFragment();
        shouldTreatFragmentAsNew = false;
        syncSuspensionState();
        return;
      }

      // Treat other seeks/jumps the same as starting playback.
      onPlay();
    };

    const onTimeUpdate = () => {
      // Capture the previous state
      const prevPlayingFragment = playingFragment;
      const prevShouldTreatFragmentAsNew = shouldTreatFragmentAsNew;

      // Update the current state
      playingFragment = getPlayingFragment();
      shouldTreatFragmentAsNew = false;

      // If the active fragment was (or should've been) visible and it isn't visible now, sync the scroll unless the user is scrolling (because that would be annoying).
      if (
        // The playing fragment was previously visible
        prevPlayingFragment?.isVisible &&
        // Following was not suspended by a manual scroll
        !isFollowingSuspended &&
        // We still have a playing fragment
        playingFragment &&
        // The playing fragment is not currently visible, or a new fragment (or one the onPlay/onSeeking handler treats as new) reaches the lower quarter
        (!playingFragment.isVisible ||
          ((playingFragment.startTime !== prevPlayingFragment.startTime ||
            prevShouldTreatFragmentAsNew) &&
            playingFragment.reachesLowerQuarter)) &&
        // The user is not currently scrolling
        !isUserScrolling
      ) {
        playingFragment = {
          ...playingFragment,
          // Optimistically mark it as visible if it isn't already, so we continue to sync the scroll
          isVisible: true,
        };
        scrollToTranscriptElement(scrollContainer, playingFragment.element);
      }

      syncSuspensionState();
    };

    resumeAutoScrollRef.current = () => {
      const currentPlayingFragment = getPlayingFragment();

      if (currentPlayingFragment) {
        // Optimistically restore the existing follow behavior before scrolling,
        // so future time updates continue tracking the active fragment.
        playingFragment = {
          ...currentPlayingFragment,
          isVisible: true,
        };
        shouldTreatFragmentAsNew = false;
        isFollowingSuspended = false;
        scrollToTranscriptElement(
          scrollContainer,
          currentPlayingFragment.element,
        );
      } else {
        playingFragment = undefined;
      }
      syncSuspensionState();
    };

    scrollContainer.addEventListener("scroll", onScroll);
    scrollContainer.addEventListener("scrollend", onScrollEnd);

    nativeAudio.addEventListener("play", onPlay);
    nativeAudio.addEventListener("seeking", onSeeking);
    nativeAudio.addEventListener("timeupdate", onTimeUpdate);

    // A system scroll already under way has to end here too where `scrollend` isn't supported
    if (scrollContainer.dataset[SCROLLING_ROLE_DATA_KEY] === "system") {
      scrollEndTimer = setTimeout(onScrollEnd, 1000);
    }

    if (shouldShowPlayheadRef.current) {
      shouldShowPlayheadRef.current = false;
      const currentFragment = getPlayingFragment();
      if (currentFragment && !currentFragment.isVisible) {
        scrollToTranscriptElement(
          scrollContainer,
          currentFragment.element,
          "instant",
        );
      }
    }

    return () => {
      scrollContainer.removeEventListener("scroll", onScroll);
      scrollContainer.removeEventListener("scrollend", onScrollEnd);

      nativeAudio.removeEventListener("play", onPlay);
      nativeAudio.removeEventListener("seeking", onSeeking);
      nativeAudio.removeEventListener("timeupdate", onTimeUpdate);

      resumeAutoScrollRef.current = () => undefined;

      if (scrollEndTimer !== undefined) {
        clearTimeout(scrollEndTimer);
      }
    };
  }, [
    containerRef,
    recordingId,
    fragments,
    nativeAudio,
    hasRequestedPosition,
    getIsMouseOver,
  ]);

  const resumeAutoScroll = useCallback(() => {
    resumeAutoScrollRef.current();
  }, []);

  return {
    isAutoScrollSuspended,
    resumeDirection,
    resumeAutoScroll,
  };
}

function useGetIsMouseOver(containerRef: RefObject<HTMLElement | null>) {
  const isMouseOverRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleMouseOver = () => {
      isMouseOverRef.current = true;
    };

    const handleMouseLeave = () => {
      isMouseOverRef.current = false;
    };

    container.addEventListener("mouseover", handleMouseOver);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mouseover", handleMouseOver);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [containerRef]);

  return useCallback(() => isMouseOverRef.current, []);
}
